import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ListGroup, Badge, Form, InputGroup, Button } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { FaMinus, FaPlus, FaTimes } from 'react-icons/fa'; // Import icons

interface Message {
  id: string;
  senderId: string;
  recipientId: string;
  content: string;
  timestamp: number;
  gameAddress: string;
  gameDate: string; // New field for game date
  gameTime: string; // New field for game time
  senderLatitude: number;
  senderLongitude: number;
  searcherId: string; // Add searcherId to Message interface
  senderName?: string; // Optional sender name
}

interface SearcherChatProps {
  searcherId: string; // The ID of the player who is searching (the sender of the initial message)
  recipientId: string; // The ID of the player who received the initial message
  searcherLatitude: number; // Searcher's current latitude
  searcherLongitude: number; // Searcher's current longitude
  searcherName: string; // Searcher's name
  gameAddress: string; // New prop for game address
  gameDate: string; // New prop for game date
  gameTime: string; // New prop for game time
  onCloseChat: () => void; // Add this prop
  isMaximized: boolean; // New prop to indicate if this chat is maximized
  onMaximizeToggle: (chatId: string | null) => void; // New prop to toggle maximization
  unreadCount: number; // New prop for unread messages count
  onNewMessage: (playerId: string) => void; // Callback for new messages
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const SearcherChat: React.FC<SearcherChatProps> = ({ searcherId, recipientId, searcherLatitude, searcherLongitude, searcherName, gameAddress, gameDate, gameTime, onCloseChat, isMaximized, onMaximizeToggle, unreadCount, onNewMessage }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [gameLocation, setGameLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([-34.6037, -58.3816]); // Default to Buenos Aires
  const [replyContent, setReplyContent] = useState<string>('');
  const [recipientName, setRecipientName] = useState<string | null>(null); // New state for recipient's name
  const messagesEndRef = useRef<HTMLDivElement>(null); // Ref for the end of messages

  useEffect(() => {
    const fetchRecipientName = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/players/${recipientId}/name`);
        if (response.ok) {
          const data = await response.json();
          setRecipientName(data.name);
        } else {
          console.error('Error fetching recipient name:', await response.json());
        }
      } catch (error) {
        console.error('Network error fetching recipient name:', error);
      }
    };

    fetchRecipientName();
  }, [recipientId, backendUrl]);

  const getMapLink = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const handleReply = async () => {
    if (!searcherId || !recipientId || !replyContent) return;

    // const lastMessage = messages[messages.length - 1]; // No longer needed
    // const gameAddress = lastMessage?.gameAddress || ''; // No longer needed
    const senderLatitude = searcherLatitude; // Use searcher's current location
    const senderLongitude = searcherLongitude; // Use searcher's current location

    try {
      const response = await fetch(`${backendUrl}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          senderId: searcherId,
          recipientId,
          content: replyContent,
          gameAddress, // Use the gameAddress prop directly
          gameDate, // Use the gameDate prop directly
          gameTime, // Use the gameTime prop directly
          senderLatitude,
          senderLongitude,
          searcherId, // The searcher is the sender in this context
          senderName: searcherName || searcherId, // Include sender name, with fallback
        }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Message sent, backend response data:', data.data);
        setReplyContent(''); // Clear input
        setMessages(prevMessages => [...prevMessages, data.data]);
      } else {
        console.error('Error sending reply:', await response.json());
      }
    } catch (error) {
      console.error('Network error sending reply:', error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let longPollTimeout: NodeJS.Timeout | undefined;

    const fetchConversationHistory = async () => {
      if (!searcherId || !recipientId) return;
      try {
        const response = await fetch(`${backendUrl}/api/messages/conversation/${searcherId}/${recipientId}`);
        const data = await response.json();
        if (isMounted && response.ok && data.messages) {
          setMessages(data.messages);
          const latestMessage = data.messages[data.messages.length - 1];
          if (latestMessage && latestMessage.senderLatitude !== undefined && latestMessage.senderLongitude !== undefined) {
            setGameLocation({ latitude: latestMessage.senderLatitude, longitude: latestMessage.senderLongitude });
            setMapCenter([latestMessage.senderLatitude, latestMessage.senderLongitude]);
          }
        }
      } catch (error) {
        console.error('Error fetching conversation history:', error);
      }
    };

    const pollForNewMessages = async () => {
      if (!searcherId || !recipientId) return; // Only poll if both IDs are available

      try {
        console.log(`SearcherChat: Initiating long poll for searcher: ${searcherId}, player: ${recipientId}`);
        const response = await fetch(`${backendUrl}/api/messages/searcher/${searcherId}/${recipientId}`);
        const data = await response.json();

        if (isMounted && response.ok && data.newMessage) {
          console.log('SearcherChat: New message received from long polling:', data.newMessage);
          setMessages(prevMessages => {
            // Check if the message already exists to prevent duplicates
            if (prevMessages.some(msg => msg.id === data.newMessage.id)) {
              return prevMessages; // Message already exists, don't add again
            }
            // No need to set gameLocation or mapCenter here, as searcher doesn't see the map in chat
            if (!isMaximized) {
              onNewMessage(recipientId); // Notify parent about new message if not maximized
            }
            return [...prevMessages, data.newMessage];
          });
        }
      } catch (error) {
        console.error('Long polling for new messages error:', error);
      } finally {
        if (isMounted) {
          longPollTimeout = setTimeout(pollForNewMessages, 1000); // Short delay to prevent hammering the server
        }
      }
    };

    fetchConversationHistory(); // Fetch history initially
    pollForNewMessages(); // Start polling for new messages

    return () => {
      isMounted = false;
      if (longPollTimeout) {
        clearTimeout(longPollTimeout);
      }
    };
  }, [searcherId, recipientId]); // Re-run effect if searcherId or recipientId changes

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]); // Scroll to bottom when messages change

  return (
    <>
      {!isMaximized ? (
        <div
          className="chat-bubble d-flex align-items-center justify-content-around"
          onClick={() => onMaximizeToggle(recipientId)}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            borderRadius: '30px',
            padding: '10px 15px',
            minWidth: '120px',
            height: '60px',
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            zIndex: 1000,
          }}
        >
          <span>{recipientName || recipientId}</span>
          {unreadCount > 0 && (
            <Badge pill bg="danger" className="ms-2">
              {unreadCount}
            </Badge>
          )}
          <FaPlus size={20} className="ms-2" />
        </div>
      ) : (
        <Card className="mt-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            {t('yourConversation')}
            <div>
              <Button variant="link" onClick={() => onMaximizeToggle(null)} className="p-0 text-dark me-2">
                <FaMinus />
              </Button>
              <Button variant="link" onClick={onCloseChat} className="p-0 text-dark">
                <FaTimes />
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {messages.length === 0 ? (
              <p>{t('noMessagesYet')}</p>
            ) : (
              <ListGroup variant="flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {messages.map((msg, index) => {
                  const isOwnMessage = msg.senderId === searcherId;
                  return (
                    <ListGroup.Item
                      key={msg.id || index}
                      className={`d-flex ${isOwnMessage ? 'justify-content-end' : 'justify-content-start'} align-items-start mb-2`}
                      style={{ backgroundColor: isOwnMessage ? '#dcf8c6' : '#e0e0e0', borderRadius: '10px', maxWidth: '75%' }}
                    >
                      <div className={`d-flex flex-column ${isOwnMessage ? 'align-items-end' : 'align-items-start'}`}>
                        <div className="fw-bold">{msg.senderName || msg.senderId}</div>
                        {msg.content}
                      </div>
                      <Badge bg="secondary" pill className="ms-2">
                        {typeof msg.timestamp === 'string'
                          ? new Date(parseInt(msg.timestamp, 10)).toLocaleTimeString()
                          : new Date(msg.timestamp).toLocaleTimeString()}
                      </Badge>
                    </ListGroup.Item>
                  );
                })}
                <div ref={messagesEndRef} />
              </ListGroup>
            )}

            

            <div className="mt-3">
              <InputGroup>
                <Form.Control
                  as="textarea"
                  rows={1}
                  placeholder={t('typeYourReply')}
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                />
                <Button variant="primary" onClick={handleReply}>
                  {t('reply')}
                </Button>
              </InputGroup>
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default SearcherChat;